import { useEffect, useState, type FormEvent } from 'react';
import { doc, getDoc, getDocs, setDoc, serverTimestamp, addDoc, collection, deleteDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Button, Input, Label, Textarea, Select, HelperText } from '../components/ui';
import { computeEffectiveDeadline } from '@scholarship-app/shared';
import { safeFormatDate } from '../lib/dates';
import { AlertTriangle, ArrowLeft, ExternalLink, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { humanize } from '../lib/humanize';
import { SchoolPicker } from '../components/SchoolPicker';
import type { Scholarship, ScholarshipStatus } from '@scholarship-app/types';

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

const STATUS_OPTIONS: ScholarshipStatus[] = [
  'identified', 'researching', 'in_progress', 'submitted', 'awarded', 'rejected', 'expired',
];

const isNewMode = (id?: string) => id === 'new';

export function ScholarshipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const nav = useNavigate();

  const [scholarship, setScholarship] = useState<Partial<Scholarship>>({
    name: '',
    sponsor: '',
    url: '',
    url_verified: false,
    amount_min: null,
    amount_max: null,
    amount_type: 'fixed',
    amount_notes: '',
    deadline: null,
    deadline_rolling: false,
    linked_admission_deadline: null,
    requires_admission_to: null,
    application_type: 'separate_application',
    status: 'identified',
    notes: '',
    starred: false,
    effort_estimate: 'M',
    tags: [],
    competitiveness: 'medium',
    competitiveness_notes: '',
    need_based: false,
    homeschool_friendly: true,
  });
  const [loading, setLoading] = useState(!isNewMode(id));
  const [saving, setSaving] = useState(false);
  // Target-school names — quick-pick options for the School field (v0.2.36).
  const [targetSchoolNames, setTargetSchoolNames] = useState<string[]>([]);

  useEffect(() => {
    async function load() {
      if (isNewMode(id) || !db || !membership || !applicantId || !id) return;
      const sref = doc(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships', id);
      const snap = await getDoc(sref);
      if (snap.exists()) {
        setScholarship(snap.data() as Scholarship);
      }
      setLoading(false);
    }
    load();
  }, [id, membership, applicantId]);

  // Load the applicant's target schools to offer as quick picks (v0.2.36).
  useEffect(() => {
    async function loadTargets() {
      if (!db || !membership || !applicantId) return;
      try {
        const col = collection(
          db, 'households', membership.householdId, 'applicants', applicantId, 'target_schools'
        );
        const snap = await getDocs(col);
        const names = snap.docs
          .map((d) => (d.data() as { school_name?: string; archived?: boolean }))
          .filter((t) => !t.archived && t.school_name)
          .map((t) => t.school_name as string);
        setTargetSchoolNames(names);
      } catch {
        setTargetSchoolNames([]);
      }
    }
    loadTargets();
  }, [membership, applicantId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!db || !membership || !applicantId || !user) return;
    setSaving(true);
    try {
      const now = serverTimestamp();
      const effective = computeEffectiveDeadline({
        deadline: scholarship.deadline ?? null,
        linked_admission_deadline: scholarship.linked_admission_deadline ?? null,
      });
      const scholarshipsCol = collection(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships');

      if (isNewMode(id)) {
        const newId = genId('sch');
        const payload: Partial<Scholarship> = {
          ...scholarship,
          scholarship_id: newId,
          effective_deadline: effective,
          created_at: now as unknown as string,
          updated_at: now as unknown as string,
          status_changed_at: now as unknown as string,
          discovered_at: now as unknown as string,
          created_by: user.uid,
          updated_by: user.uid,
          archived: false,
          source_discovered_via: 'manual',
          applied_with_essay_version_ids: [],
          linked_documents: [],
          linked_rec_request_ids: [],
          requirements: scholarship.requirements ?? {
            essay_required: false, essay_prompts: [], essay_word_counts: [],
            letters_of_rec_required: 0, letter_of_rec_restrictions: [],
            transcript_required: false, test_scores_required: false, min_test_scores: {},
            portfolio_required: false, headshot_required: false, interview_required: false,
            other: '',
          },
        };
        await setDoc(doc(scholarshipsCol, newId), payload);
        await addDoc(collection(scholarshipsCol, newId, 'activity'), {
          event_type: 'created',
          actor_user_id: user.uid,
          actor_role: membership.role,
          actor_display_name: user.displayName ?? user.email,
          occurred_at: now,
          note: 'Created manually.',
        });
        nav(`/scholarships/${newId}`);
      } else if (id) {
        const sref = doc(scholarshipsCol, id);
        await setDoc(sref, {
          ...scholarship,
          effective_deadline: effective,
          updated_at: now,
          updated_by: user.uid,
        }, { merge: true });
        nav('/scholarships');
      }
    } catch (e) {
      console.error('Save failed', e);
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!db || !membership || !applicantId || !user || !id || isNewMode(id)) return;
    const action = scholarship.archived ? 'unarchive' : 'archive';
    if (!confirm(`Are you sure you want to ${action} "${scholarship.name}"? You can ${scholarship.archived ? 'archive it again later' : 'unarchive it later from the "Show archived" filter'}.`)) {
      return;
    }
    setSaving(true);
    try {
      const sref = doc(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships', id);
      await updateDoc(sref, {
        archived: !scholarship.archived,
        updated_at: serverTimestamp(),
        updated_by: user.uid,
      });
      await addDoc(collection(sref, 'activity'), {
        event_type: scholarship.archived ? 'unarchived' : 'archived',
        actor_user_id: user.uid,
        actor_role: membership.role,
        actor_display_name: user.displayName ?? user.email,
        occurred_at: serverTimestamp(),
        note: scholarship.archived ? 'Unarchived' : 'Archived',
      });
      nav('/scholarships');
    } catch (e) {
      console.error('Archive failed', e);
      alert('Archive failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  async function handleHardDelete() {
    if (!db || !membership || !applicantId || !id || isNewMode(id)) return;
    if (membership.role !== 'parent') {
      alert('Only parents can permanently delete scholarships.');
      return;
    }
    if (!confirm(`PERMANENTLY DELETE "${scholarship.name}"?\n\nThis cannot be undone. The scholarship and its activity log will be removed.\n\nIf you only want to hide it, click Cancel and use Archive instead.`)) {
      return;
    }
    setSaving(true);
    try {
      const sref = doc(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships', id);
      // Delete the parent doc. The onScholarshipDeleted Cloud Function will
      // cascade-clean the activity subcollection in the background.
      await deleteDoc(sref);
      nav('/scholarships');
    } catch (e) {
      console.error('Delete failed', e);
      alert('Delete failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-soft">Loading…</div>;
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <button onClick={() => nav('/scholarships')} className="text-sm text-soft hover:text-ink flex items-center gap-1">
        <ArrowLeft size={14} /> Back to scholarships
      </button>

      <h1 className="text-2xl font-semibold text-ink">
        {isNewMode(id) ? 'Add scholarship' : scholarship.name}
      </h1>

      {!isNewMode(id) && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="indigo">{humanize(scholarship.status)}</Badge>
          {scholarship.archived && <Badge variant="gray">Archived</Badge>}
          {scholarship.url && (
            <a href={scholarship.url} target="_blank" rel="noopener noreferrer" className="text-sm text-forest-700 hover:underline flex items-center gap-1">
              <ExternalLink size={12} /> Open scholarship page
            </a>
          )}
          {scholarship.url_verified === false && (
            <Badge variant="amber">URL not verified — check before applying</Badge>
          )}
        </div>
      )}

      {scholarship.linked_admission_deadline && scholarship.deadline && scholarship.linked_admission_deadline !== scholarship.deadline && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex gap-2 text-amber-900">
            <AlertTriangle size={20} className="shrink-0" />
            <div className="text-sm">
              <strong className="block">Admission-linked deadline</strong>
              To be considered for this scholarship, the admission application to {scholarship.requires_admission_to} must be submitted by <strong>{safeFormatDate(scholarship.linked_admission_deadline, 'MMMM d, yyyy') ?? scholarship.linked_admission_deadline}</strong> — earlier than the scholarship's own deadline of {safeFormatDate(scholarship.deadline, 'MMMM d, yyyy') ?? scholarship.deadline}.
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="p-4 space-y-4">
          <h2 className="font-medium text-ink">Basics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={scholarship.name ?? ''} onChange={(e) => setScholarship({ ...scholarship, name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="sponsor">Sponsor</Label>
              <Input id="sponsor" value={scholarship.sponsor ?? ''} onChange={(e) => setScholarship({ ...scholarship, sponsor: e.target.value })} required />
            </div>
          </div>
          <div>
            <Label htmlFor="url">URL</Label>
            <Input id="url" type="url" value={scholarship.url ?? ''} onChange={(e) => setScholarship({ ...scholarship, url: e.target.value })} />
            <div className="mt-2 flex items-center gap-2">
              <input
                id="url_verified"
                type="checkbox"
                checked={scholarship.url_verified ?? false}
                onChange={(e) => setScholarship({ ...scholarship, url_verified: e.target.checked })}
              />
              <label htmlFor="url_verified" className="text-sm text-soft">URL verified manually</label>
            </div>
          </div>
        </Card>

        <Card className="p-4 space-y-4">
          <h2 className="font-medium text-ink">Amount</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="amount_type">Type</Label>
              <Select id="amount_type" value={scholarship.amount_type ?? 'fixed'} onChange={(e) => setScholarship({ ...scholarship, amount_type: e.target.value as Scholarship['amount_type'] })}>
                <option value="fixed">Fixed</option>
                <option value="range">Range</option>
                <option value="full_ride">Full Ride</option>
                <option value="renewable">Renewable</option>
                <option value="varies">Varies</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount_min">Min ($)</Label>
              <Input id="amount_min" type="number" value={scholarship.amount_min ?? ''} onChange={(e) => setScholarship({ ...scholarship, amount_min: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div>
              <Label htmlFor="amount_max">Max ($)</Label>
              <Input id="amount_max" type="number" value={scholarship.amount_max ?? ''} onChange={(e) => setScholarship({ ...scholarship, amount_max: e.target.value ? Number(e.target.value) : null })} />
            </div>
          </div>
          <div>
            <Label htmlFor="amount_notes">Amount Notes</Label>
            <Textarea id="amount_notes" rows={2} value={scholarship.amount_notes ?? ''} onChange={(e) => setScholarship({ ...scholarship, amount_notes: e.target.value })} />
          </div>
        </Card>

        <Card className="p-4 space-y-4">
          <h2 className="font-medium text-ink">Deadlines & School Linkage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="deadline">Scholarship Deadline</Label>
              <Input id="deadline" type="date" value={scholarship.deadline ?? ''} onChange={(e) => setScholarship({ ...scholarship, deadline: e.target.value || null })} />
            </div>
            <div>
              <Label htmlFor="linked">Admission-Linked Deadline</Label>
              <Input id="linked" type="date" value={scholarship.linked_admission_deadline ?? ''} onChange={(e) => setScholarship({ ...scholarship, linked_admission_deadline: e.target.value || null })} />
              <HelperText>Required only if applying for the scholarship requires meeting a school's admission deadline.</HelperText>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="requires_admission_to">School</Label>
              <SchoolPicker id="requires_admission_to" value={scholarship.requires_admission_to ?? null} targetSchoolNames={targetSchoolNames} onChange={(v) => setScholarship({ ...scholarship, requires_admission_to: v })} />
            </div>
            <div>
              <Label htmlFor="application_type">Application Type</Label>
              <Select id="application_type" value={scholarship.application_type ?? 'separate_application'} onChange={(e) => setScholarship({ ...scholarship, application_type: e.target.value as Scholarship['application_type'] })}>
                <option value="auto_considered">Auto-Considered with Admission</option>
                <option value="separate_application">Separate Application</option>
                <option value="requires_admission_first">Requires Admission First</option>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-4 space-y-4">
          <h2 className="font-medium text-ink">Status & Tracking</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select id="status" value={scholarship.status ?? 'identified'} onChange={(e) => {
                const newStatus = e.target.value as ScholarshipStatus;
                // Auto-sync `result` from `status` for terminal states.
                let newResult: Scholarship['result'] = scholarship.result ?? null;
                if (newStatus === 'awarded') newResult = 'awarded';
                else if (newStatus === 'rejected') newResult = 'rejected';
                else if (newStatus === 'expired') newResult = null;
                else if (newStatus !== 'submitted') newResult = null; // back to in-progress states clear result

                // When moving INTO 'awarded', seed result_amount with amount_max
                // as a starting point if it's not already set.
                let newResultAmount = scholarship.result_amount ?? null;
                if (newStatus === 'awarded' && (newResultAmount == null || newResultAmount === 0)) {
                  newResultAmount = scholarship.amount_max ?? scholarship.amount_min ?? null;
                }
                // When moving OUT of awarded, clear result_amount so it doesn't
                // pollute the dashboard total.
                if (scholarship.status === 'awarded' && newStatus !== 'awarded') {
                  newResultAmount = null;
                }

                setScholarship({
                  ...scholarship,
                  status: newStatus,
                  result: newResult,
                  result_amount: newResultAmount,
                });
              }}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="effort">Effort</Label>
              <Select id="effort" value={scholarship.effort_estimate ?? 'M'} onChange={(e) => setScholarship({ ...scholarship, effort_estimate: e.target.value as 'S' | 'M' | 'L' })}>
                <option value="S">Small — Quick (no essay)</option>
                <option value="M">Medium — Essay required</option>
                <option value="L">Large — Full package (essays, recs, portfolio)</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="competitiveness">Competitiveness</Label>
              <Select id="competitiveness" value={scholarship.competitiveness ?? 'medium'} onChange={(e) => setScholarship({ ...scholarship, competitiveness: e.target.value as Scholarship['competitiveness'] })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="extremely_high">Extremely High</option>
              </Select>
            </div>
          </div>
          {scholarship.status === 'awarded' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <Label htmlFor="result_amount">Awarded Amount ($)</Label>
              <Input
                id="result_amount"
                type="number"
                min="0"
                step="1"
                value={scholarship.result_amount ?? ''}
                onChange={(e) => setScholarship({ ...scholarship, result_amount: e.target.value ? Number(e.target.value) : null })}
                placeholder="e.g. 2500"
              />
              <HelperText>
                The actual amount received (may differ from the advertised min/max range above).
                This value is summed into the Awarded total on the Dashboard.
              </HelperText>
            </div>
          )}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" rows={3} value={scholarship.notes ?? ''} onChange={(e) => setScholarship({ ...scholarship, notes: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input id="starred" type="checkbox" checked={scholarship.starred ?? false} onChange={(e) => setScholarship({ ...scholarship, starred: e.target.checked })} />
            <label htmlFor="starred" className="text-sm text-soft">Star this scholarship</label>
          </div>
        </Card>

        <div className="flex justify-between items-center gap-2 flex-wrap">
          {!isNewMode(id) && (
            <div className="flex gap-2 flex-wrap">
              <Button type="button" variant="ghost" onClick={handleArchive} disabled={saving}>
                {scholarship.archived ? (
                  <><ArchiveRestore size={14} className="mr-1" />Unarchive</>
                ) : (
                  <><Archive size={14} className="mr-1" />Archive</>
                )}
              </Button>
              {membership?.role === 'parent' && (
                <Button type="button" variant="danger" onClick={handleHardDelete} disabled={saving}>
                  <Trash2 size={14} className="mr-1" />Delete permanently
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <Button type="button" variant="secondary" onClick={() => nav('/scholarships')}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
